import { Form } from 'react-final-form';
import { useTranslation } from 'next-i18next';
import {
  Box,
  BoxProps,
  CSSObject,
  Grid,
  FormControl,
  FormLabel,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { SearchRealtorsAgencies as Props } from '@/types';
import { Realtor, Agency } from '@/types/agency';
import { mapAgenciesToTeasers } from './ListAgencies';
import Realtors from '@/components/frontend/Realtors';
import ShowMoreSection from '../ShowMoreSection';
import { TeaserCard } from '../Teaser';
import FormField from '../Form/FormField';
import Heading from '../Heading';
import { OptionProp } from '@/lib/optionProps';
import { matchesLocalizedRegionNames } from '@/lib/regions';
import voidFn from '@/lib/voidFn';

const initialValues = {
  name: '',
  location: '',
  agency: '',
  lang: '',
};

type SearchValues = typeof initialValues;

// CSS for creating a ball with number as :after -pseudo-element in the DOM
const pseudoBall = (count: number): CSSObject => ({
  content: `"${count}"`,
  display: 'inline-block',
  verticalAlign: '14%',
  ml: 4,
  bg: 'highlightBackground',
  color: 'highlightColor',
  borderRadius: '50%',
  textAlign: 'center',
  fontSize: '.75rem',
  height: '2em',
  width: '2em',
  lineHeight: '2em',
});

const filterAgencies = (
  agencies: Agency[],
  { agency, location }: SearchValues,
  locale?: string,
): Agency[] =>
  agencies.filter(({ id, municipality, service_area }) => {
    if (agency && id !== Number.parseInt(agency, 10)) {
      return false;
    }
    if (
      location &&
      municipality &&
      // Support searching for agencies using city name in both fi and sv.
      // The city name is entered in the backoffice as free text, and is
      // tied in language with the street name, so it doesn't make sense
      // to translate the city name (but not the street name) to Swedish,
      // but we can still support the Swedish city name as a search value
      !matchesLocalizedRegionNames([municipality], [locale, 'fi'], location) &&
      // support searching for agencies using the service area in both languages
      !matchesLocalizedRegionNames(
        service_area as string[],
        [locale, 'fi'],
        location,
      )
    ) {
      return false;
    }
    return true;
  });

const filterRealtors = (
  realtors: Realtor[],
  { name, location, agency, lang }: SearchValues,
  locale?: string,
): Realtor[] =>
  realtors.filter(
    ({
      real_estate_agency_id,
      name: realtorName,
      working_area,
      profile_language_skills,
    }) => {
      // Show supervisors only when no specific agency is selected
      if (agency && !real_estate_agency_id) {
        return false;
      }
      if (
        agency &&
        real_estate_agency_id &&
        Number.parseInt(agency, 10) !== real_estate_agency_id
      ) {
        return false;
      }
      // NOTE: casting is fine. language_skills is JSONB
      if (
        lang &&
        profile_language_skills &&
        !(profile_language_skills as string[])
          .map((s) => s?.toLowerCase())
          .includes(lang.toLowerCase())
      ) {
        return false;
      }
      // NOTE: casting is fine. working_area is JSONB
      if (
        location &&
        // Support searching for realtors using working area names in both fi and sv
        !matchesLocalizedRegionNames(
          working_area as string[],
          [locale, 'fi'],
          location,
        )
      ) {
        return false;
      }
      if (name && realtorName && !new RegExp(name, 'i').test(realtorName)) {
        return false;
      }

      return true;
    },
  );

const FilterForm = ({
  agencies,
  ...rest
}: Pick<Props, 'agencies'> & BoxProps) => {
  const { t } = useTranslation('frontoffice');

  const agenciesOptions: OptionProp<string>[] = [
    { label: t('all', 'Kaikki'), value: '' },
    ...agencies.map(({ name, id }) => ({ label: name, value: `${id}` })),
  ];

  const languageOptions: OptionProp<string>[] = [
    { value: '', label: t('all', 'Kaikki') },
    { value: 'fi', label: t('languages.fi', 'Suomi') },
    { value: 'sv', label: t('languages.sv', 'Ruotsi') },
    { value: 'en', label: t('languages.en', 'Englanti') },
    { value: 'ru', label: t('languages.ru', 'Venäjä') },
  ];

  return (
    <Box as="form" {...rest}>
      <FormControl as="fieldset">
        <FormLabel as="legend" mb="6">
          <Heading variant="subtitle">
            {t(
              'search_realtors_agencies_title',
              'Löydä lähin välittäjä tai välitysliike',
            )}
          </Heading>
        </FormLabel>
        <Grid
          layerStyle="grid"
          gridTemplateColumns={[
            'repeat(auto-fit, minmax(220px, 500px))',
            null,
            'repeat(auto-fit, minmax(500px, 1fr))',
          ]}
        >
          <Grid layerStyle="smGrid" sx={{ '> *': { width: 'auto' } }}>
            <FormField
              name="location"
              placeholder={t('location_placeholder_realtors', 'Kunta')}
              label={t('location', 'Sijainti')}
            />
            <FormField
              name="name"
              placeholder={t('name_placeholder', 'Nimi tai sen osa')}
              label={t('realtor_name', 'Välittäjän nimi')}
            />
          </Grid>
          <Grid layerStyle="smGrid" sx={{ '> *': { width: 'auto' } }}>
            <FormField
              name="agency"
              label={t('agency', 'Välitysliike')}
              type="select"
              options={agenciesOptions}
            />
            <FormField
              name="lang"
              label={t('language', 'Kieli')}
              type="select"
              options={languageOptions}
            />
          </Grid>
        </Grid>
      </FormControl>
    </Box>
  );
};

const initialDisplayItemsLimit = 12;

const SearchRealtorsAgencies = ({ agencies, realtors }: Props) => {
  const { t } = useTranslation('frontoffice');
  const { locale } = useRouter();

  const realtorsTitle = t(
    'search_realtors_agencies_realtors_title',
    'Välittäjät ja muut asiantuntijat',
  );
  const realtorsNoResults = t(
    'search_realtors_agencies_realtors_no_result',
    'Hakutermeillä ei löytynyt välittäjiä',
  );

  const agenciesTitle = t(
    'search_realtors_agencies_agencies_title',
    'Välitysliikkeet',
  );
  const agenciesNoResults = t(
    'search_realtors_agencies_agencies_no_result',
    'Hakutermeillä ei löytynyt välitysliikkeitä',
  );

  return (
    <Form<SearchValues>
      onSubmit={voidFn}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => {
        const filteredRealtors = filterRealtors(realtors, values, locale);
        const filteredAgencies = filterAgencies(agencies, values, locale);

        return (
          <>
            <FilterForm
              agencies={agencies}
              onSubmit={handleSubmit}
              bg="white"
              p={[5, 7, 8]}
              borderRadius="lg"
              boxShadow="md"
            />
            <Box>
              <Heading
                variant="h5"
                mb={[3, 5]}
                _after={
                  realtors.length > initialDisplayItemsLimit
                    ? pseudoBall(realtors.length)
                    : undefined
                }
              >
                {realtorsTitle}
              </Heading>
              {filteredRealtors.length ? (
                <Realtors
                  users={filteredRealtors}
                  limit={initialDisplayItemsLimit}
                />
              ) : (
                <Text>{realtorsNoResults}</Text>
              )}
            </Box>
            <Box>
              <Heading
                variant="h5"
                mb={[3, 5]}
                _after={
                  agencies.length > initialDisplayItemsLimit
                    ? pseudoBall(agencies.length)
                    : undefined
                }
              >
                {agenciesTitle}
              </Heading>
              {filteredAgencies.length ? (
                <ShowMoreSection
                  as={Grid}
                  layerStyle="grid"
                  limit={initialDisplayItemsLimit}
                  gridAutoRows="1fr"
                >
                  {mapAgenciesToTeasers(filteredAgencies, t).map(
                    ({ heading, id, ...teaser }) => (
                      <TeaserCard
                        {...teaser}
                        heading={{ ...heading, variant: 'h5' }}
                        key={id}
                      />
                    ),
                  )}
                </ShowMoreSection>
              ) : (
                <Text>{agenciesNoResults}</Text>
              )}
            </Box>
          </>
        );
      }}
    />
  );
};

export default SearchRealtorsAgencies;
